import { AxiosResponse } from "axios";
import { format } from "date-fns";
import useSWR, { KeyedMutator } from "swr";
import { AttributeFilters } from "@/models/attributeFilter";
import { UploadHistory } from "@/models/uploadHistory";
import { Voc } from "@/models/Voc";
import { VocCount, VocCountData } from "@/models/vocCount";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import {
  UploadHisotriesResponse,
  UploadHistorySchema,
  VocCountSchema,
  VocSearchRequest,
  VocSearchResponse,
  VocTotalCharCountSchema,
} from "@/services/voc/voc.api.types";
import VocEndpoints from "@/services/voc/voc.endpoint";

type VocSearchParams = {
  workspaceId: string;
  keyword: string;
  categories?: string[];
  startDate?: Date;
  endDate?: Date;
  attributeFilters?: AttributeFilters;
  tagIds?: string[];
};

export class VocService {
  static async getVocCharCount(
    workspaceId: string,
    start: Date,
    end: Date,
  ): Promise<number> {
    const startStr = format(start, "yyyy-MM-dd");
    const endStr = format(end, "yyyy-MM-dd");
    const result = await ApiService.fetchData<undefined, VocTotalCharCountSchema>({
      url: VocEndpoints.GET_VOC_CHAR_COUNT(startStr, endStr, workspaceId),
      method: "GET",
    });
    return result.data.totalCharCount ?? 0;
  }

  static getVocCountData(
    workspaceId: string | null,
    displayLayerLevel: number,
  ): {
    data: VocCountData | undefined;
    error: unknown;
    isLoading: boolean;
  } {
    const fetcher = () =>
      ApiService.fetchData<undefined, VocCountSchema[]>({
        url: VocEndpoints.GET_VOC_COUNTS(workspaceId),
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading } = useSWR(
      // 第1引数がnullの場合はAPI呼び出しがスキップされる
      workspaceId ? VocEndpoints.GET_VOC_COUNTS(workspaceId) : null,
      fetcher,
      defaultSWROption,
    );

    return {
      data: data?.data && buildVocCountData(data.data, displayLayerLevel),
      error,
      isLoading,
    };
  }

  static getUploadHistories(
    workspaceId: string,
    offset: number,
    limit = 10,
  ): {
    data: UploadHistory[] | undefined;
    total: number | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<UploadHisotriesResponse, undefined>>;
  } {
    const url = VocEndpoints.GET_UPLOAD_HISTORIES(offset, limit, workspaceId);

    const fetcher = () => ApiService.fetchData<undefined, UploadHisotriesResponse>({ url, method: "GET" });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(
      workspaceId ? url : null,
      fetcher,
      defaultSWROption,
    );

    return {
      data: data?.data.histories.map(buildUploadHistory),
      total: data?.data.total,
      error,
      isLoading,
      mutate,
    };
  }

  static async search({
    workspaceId,
    keyword,
    categories,
    startDate,
    endDate,
    attributeFilters,
    tagIds,
  }: VocSearchParams): Promise<{
    vocs: Voc[];
    total: number;
    keyword: string;
    categories: string[];
    startDate: Date | null;
    endDate: Date | null;
  }> {
    const result = await ApiService.fetchData<
      VocSearchRequest,
      VocSearchResponse
    >({
      url: VocEndpoints.SEARCH(workspaceId),
      method: "POST",
      data: {
        keyword,
        ...(categories && categories.length > 0 && { categories }),
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() }),
        ...(attributeFilters && Object.keys(attributeFilters).length > 0 && { attributeFilters }),
        ...(tagIds && tagIds.length > 0 && { tagIds }),
      },
    });

    return {
      vocs: result.data.vocs.map((voc) =>
        new Voc(
          voc.vocId,
          voc.categories.length > 0 ? voc.categories[0].category : "",
          new Date(voc.recordedAt),
          voc.vocText,
        )
      ),
      total: result.data.total,
      keyword: result.data.keyword,
      categories: result.data.categories,
      startDate: result.data.startDate ? new Date(result.data.startDate) : null,
      endDate: result.data.endDate ? new Date(result.data.endDate) : null,
    };
  }

  static async downloadSearchResult({
    workspaceId,
    keyword,
    categories,
    startDate,
    endDate,
  }: VocSearchParams): Promise<Blob> {
    const response = await ApiService.fetchData<VocSearchRequest, Blob>({
      url: VocEndpoints.DOWNLOAD_SEARCH_RESULT(workspaceId),
      method: "POST",
      data: {
        keyword,
        ...(categories && categories.length > 0 && { categories }),
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() }),
      },
      responseType: "blob",
    });

    return response.data;
  }
}

const buildVocCountData = (
  vocCounts: VocCountSchema[],
  displayLayerLevel: number,
) => {
  const getLevel = (path: string) => path.split("/").slice(1, -1).length;

  const counts = vocCounts
    .filter((vc) => getLevel(vc.category_path) === displayLayerLevel)
    .map(buildVocCount);

  const latestYearMonth = counts.length
    ? new Date(Math.max(...counts.map((vc) => vc.date.getTime())))
    : new Date();

  return new VocCountData(counts, latestYearMonth);
};

const buildVocCount = (vocCount: VocCountSchema) => {
  return new VocCount(
    vocCount.category_path,
    vocCount.year,
    vocCount.month,
    vocCount.count,
  );
};

const buildUploadHistory = (history: UploadHistorySchema) => {
  return new UploadHistory(
    history.id,
    history.filePath,
    history.status,
    new Date(history.createdAt),
    history.failedReason ?? undefined,
    history.completedAt ? new Date(history.completedAt) : undefined,
  );
};
